import { Platform, StyleSheet } from 'react-native';
import { createTableHeadStyles } from '../../../components/table/tableHeadStyles';
import { GTFonts } from '../../../theme';
import { WEB_MAX_CONTENT_WIDTH } from '../../../theme/webLayoutWidths';
import { fs, s, v } from '../../../utils/libs';

const isWeb = Platform.OS === 'web';
const pointer = isWeb ? ({ cursor: 'pointer' } as any) : {};

/** Styles for the Inventory list, its filter sheets and the Stock value screen. */
export const createInventoryStyles = (theme: any) => {
  const tableHead = createTableHeadStyles(theme);
  return StyleSheet.create({
    screen: {
      flex: 1,
      backgroundColor: theme.colors.background,
    },
    container: {
      flex: 1,
      ...(isWeb && {
        maxWidth: WEB_MAX_CONTENT_WIDTH,
        alignSelf: 'center',
        width: '100%',
      }),
    },

    // ---------- white section under the header ----------
    headerSection: {
      backgroundColor: theme.colors.surface,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.borderGray200,
      paddingBottom: v(6),
      ...(isWeb && { paddingHorizontal: s(24) }),
    },
    // Stock value: title (opens and closes the figures) and "View
    // breakdown", then the two values as headlines when open
    strip: {
      gap: v(8),
      paddingHorizontal: s(14),
      paddingTop: v(10),
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.borderSecondary,
    },
    // Closed: the title row alone, as much space below it as above
    stripCollapsed: {
      paddingBottom: v(12),
    },
    stripHead: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: s(8),
    },
    stripToggle: {
      flexShrink: 1,
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(4),
      ...pointer,
    },
    stripTitle: {
      flexShrink: 1,
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.text,
    },
    // The two values sit on a grey panel; the title and link stay on white
    stripTiles: {
      flexDirection: 'row',
      paddingVertical: v(10),
      paddingHorizontal: s(12),
      borderRadius: s(10),
      backgroundColor: theme.colors.surfaceSecondary,
      ...pointer,
    },
    stripTile: {
      flex: 1,
      minWidth: 0,
      gap: v(2),
    },
    // Wide web windows: the values sit side by side instead of splitting the width
    stripTileWide: {
      flexGrow: 0,
      flexShrink: 1,
      flexBasis: 'auto',
      paddingRight: s(24),
    },
    stripTileDivider: {
      borderLeftWidth: 1,
      borderLeftColor: theme.colors.border,
      paddingLeft: s(16),
    },
    stripKey: {
      fontFamily: GTFonts.regular,
      fontSize: fs(12.5),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
    },
    stripValue: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(19),
      lineHeight: fs(24),
      // letterSpacing: -0.2,
      color: theme.colors.text,
    },
    stripLink: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(2),
      ...pointer,
    },
    stripLinkText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(13),
      lineHeight: fs(18),
      color: theme.colors.primary,
    },
    toolbar: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(8),
      paddingHorizontal: s(12),
      paddingTop: v(6),
    },
    count: {
      flex: 1,
      minWidth: 0,
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(20),
      color: theme.colors.text,
    },
    toolButton: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(6),
      minHeight: v(36),
      paddingHorizontal: s(12),
      borderRadius: theme.borderRadius.md,
      // Filled grey, no outline
      backgroundColor: theme.colors.neutralTileBg,
      ...pointer,
    },
    toolButtonActive: {
      opacity: 0.75,
    },
    toolButtonText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(13.5),
      lineHeight: fs(18),
      color: theme.colors.textGray700,
    },
    badge: {
      minWidth: s(20),
      height: s(20),
      borderRadius: s(10),
      paddingHorizontal: s(6),
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.colors.primaryDark,
    },
    badgeText: {
      fontFamily: GTFonts.bold,
      fontSize: fs(11.5),
      lineHeight: fs(14),
      color: theme.colors.textInverse,
    },
    applied: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: s(8),
      paddingHorizontal: s(12),
      paddingTop: v(10),
    },
    token: {
      flexDirection: 'row',
      alignItems: 'center',
      minHeight: v(32),
      paddingLeft: s(10),
      paddingRight: s(3),
      borderRadius: s(8),
      borderWidth: 1,
      borderColor: theme.colors.border,
      backgroundColor: theme.colors.surface,
    },
    tokenText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(12.5),
      lineHeight: fs(16),
      color: theme.colors.text,
    },
    tokenKey: {
      fontFamily: GTFonts.regular,
      color: theme.colors.textSecondary,
    },
    tokenRemove: {
      width: s(26),
      height: s(26),
      borderRadius: s(13),
      alignItems: 'center',
      justifyContent: 'center',
      marginLeft: s(2),
      ...pointer,
    },
    clearLink: {
      paddingVertical: v(6),
      paddingHorizontal: s(4),
      ...pointer,
    },
    clearLinkText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(13),
      lineHeight: fs(18),
      color: theme.colors.errorDarker,
    },

    // ---------- chips (status, categories, show only) ----------
    chip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(6),
      minHeight: v(38),
      paddingHorizontal: s(13),
      borderRadius: s(20),
      borderWidth: 1.5,
      borderColor: theme.colors.border,
      backgroundColor: theme.colors.surface,
      ...pointer,
    },
    chipSelected: {
      backgroundColor: theme.colors.primarySurface,
      borderColor: theme.colors.primaryDark,
      paddingLeft: s(10),
    },
    chipText: {
      // fontFamily: GTFonts.semibold,
      fontSize: fs(13),
      lineHeight: fs(18),
      color: theme.colors.text,
    },
    chipTextSelected: {
      color: theme.colors.primaryDark,
    },
    chipCount: {
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(18),
      color: theme.colors.textSecondary,
    },
    chipCountSelected: {
      color: theme.colors.primaryDark,
    },
    chipWrap: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: s(8),
    },

    // ---------- table card ----------
    card: {
      flex: 1,
      marginHorizontal: s(5),
      marginTop: v(12),
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.md,
      ...theme.shadows.sm,
      ...(isWeb && { marginHorizontal: s(24) }),
    },
    // Clips the rows to the card's corners; kept off `card` so iOS still draws its shadow
    cardClip: {
      flex: 1,
      borderRadius: s(12),
      overflow: 'hidden',
    },
    listHead: {
      ...tableHead.head,
      flexDirection: 'row',
      alignItems: 'center',
      // No left padding: the "#" column starts at the card edge
      paddingRight: s(14),
    },
    // Two columns of rows on web: each half lines up with the rows below it
    listHeadGrid: {
      alignItems: 'stretch',
      paddingRight: 0,
    },
    listHeadHalf: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      paddingRight: s(16),
    },
    listHeadHalfDivider: {
      borderRightWidth: 1,
      borderRightColor: theme.colors.border,
    },
    listHeadText: tableHead.headText,
    // The "#" column runs from the card edge to the image, so the numbers sit
    // midway between them (the old padding plus the old 26px column)
    columnIndex: {
      width: s(40),
      textAlign: 'center',
    },
    columnIndexInGrid: {
      width: s(42),
    },
    columnProduct: {
      flex: 1,
      minWidth: 0,
    },
    columnRight: {
      textAlign: 'right',
    },
    columnWrapper: {
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.border,
    },
    row: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      paddingRight: s(14),
      paddingVertical: v(12),
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.border,
      backgroundColor: theme.colors.surface,
      ...pointer,
    } as any,
    // Rows in the web grid: half width, divided by a right border, the
    // bottom border coming from `columnWrapper`
    rowInGrid: {
      // Exactly half the card, so a lone last row keeps the column line
      flexGrow: 0,
      flexShrink: 0,
      flexBasis: '50%',
      borderBottomWidth: 0,
      borderRightWidth: 1,
      borderRightColor: theme.colors.border,
      paddingRight: s(16),
      paddingVertical: v(14),
    },
    rowHovered: {
      backgroundColor: theme.colors.surfaceSecondary,
    },
    rowPressed: {
      opacity: 0.75,
    },
    // Centered under "#" and against the whole row's height
    rowIndex: {
      width: s(40),
      alignSelf: 'center',
      textAlign: 'center',
      fontFamily: GTFonts.semibold,
      fontSize: fs(13.5),
      lineHeight: fs(20),
      color: theme.colors.textGray600,
    },
    rowIndexInGrid: {
      width: s(42),
    },
    thumb: {
      width: s(44),
      height: s(44),
      borderRadius: theme.borderRadius.md,
      backgroundColor: theme.colors.surfaceSecondary,
      marginRight: s(12),
    },
    thumbPlaceholder: {
      width: s(44),
      height: s(44),
      borderRadius: theme.borderRadius.md,
      backgroundColor: theme.colors.surfaceSecondary,
      borderWidth: 1,
      borderStyle: 'dashed',
      borderColor: theme.colors.primaryLight,
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: s(12),
    },
    rowInfo: {
      flex: 1,
      minWidth: 0,
    },
    rowName: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(15),
      lineHeight: fs(20),
      color: theme.colors.text,
    },
    rowMeta: {
      marginTop: v(2),
      fontFamily: GTFonts.regular,
      fontSize: fs(12.5),
      lineHeight: fs(17),
      color: theme.colors.textSecondary,
    },
    // "2 variants" in blue, as the list has always shown it
    rowMetaVariants: {
      color: theme.colors.infoDark,
    },
    rowNote: {
      marginTop: v(3),
      fontFamily: GTFonts.semibold,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.errorDark,
    },
    rowRight: {
      flexShrink: 0,
      maxWidth: '46%',
      marginLeft: s(10),
      alignItems: 'flex-end',
    },
    rowQty: {
      textAlign: 'right',
      fontFamily: GTFonts.semibold,
      fontSize: fs(15),
      lineHeight: fs(20),
      color: theme.colors.text,
    },
    rowQtyDanger: {
      color: theme.colors.errorDark,
    },
    rowQtyAffix: {
      fontFamily: GTFonts.regular,
      fontSize: fs(12.5),
      color: theme.colors.textSecondary,
    },
    rowPrice: {
      marginTop: v(3),
      textAlign: 'right',
      fontFamily: GTFonts.regular,
      fontSize: fs(11),
      lineHeight: fs(18),
      color: theme.colors.text,
    },
    footerLoading: {
      paddingVertical: v(12),
      alignItems: 'center',
      justifyContent: 'center',
    },

    // ---------- empty states ----------
    empty: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      gap: v(8),
      paddingHorizontal: s(32),
      paddingVertical: v(48),
    },
    emptyIcon: {
      width: s(64),
      height: s(64),
      borderRadius: s(32),
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.colors.surfaceSecondary,
      marginBottom: v(6),
    },
    emptyTitle: {
      fontFamily: GTFonts.bold,
      fontSize: fs(17),
      lineHeight: fs(22),
      color: theme.colors.text,
      textAlign: 'center',
    },
    emptyText: {
      maxWidth: s(300),
      fontFamily: GTFonts.regular,
      fontSize: fs(14),
      lineHeight: fs(20),
      color: theme.colors.textSecondary,
      textAlign: 'center',
    },
    emptyActions: {
      marginTop: v(10),
      alignItems: 'center',
      gap: v(4),
    },

    // ---------- buttons ----------
    button: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: s(8),
      minHeight: v(48),
      paddingHorizontal: s(20),
      borderRadius: theme.borderRadius.md,
      ...pointer,
    },
    buttonPrimary: {
      backgroundColor: theme.colors.primaryDark,
    },
    buttonGhost: {
      backgroundColor: theme.colors.surface,
      borderWidth: 1.5,
      borderColor: theme.colors.border,
    },
    buttonPressed: {
      opacity: 0.8,
    },
    buttonDisabled: {
      opacity: 0.5,
    },
    buttonText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(15),
      lineHeight: fs(20),
      color: theme.colors.text,
    },
    buttonTextPrimary: {
      color: theme.colors.textInverse,
    },
    // The chevron sits close to the label, as on "View N unpaid orders"
    buttonTrailingIcon: {
      marginLeft: -s(4),
    },
    textLink: {
      minHeight: v(40),
      justifyContent: 'center',
      paddingHorizontal: s(8),
      ...pointer,
    },
    textLinkText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(20),
      color: theme.colors.primaryDark,
    },

    // ---------- sheets / panel ----------
    // Sheet titles use components/Reusables/bottomSheet/SheetHeader.
    sheetHeaderNote: {
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(18),
      color: theme.colors.textSecondary,
      paddingRight: s(8),
    },
    sheetBody: {
      paddingHorizontal: s(20),
      paddingTop: v(18),
      paddingBottom: v(24),
      gap: v(24),
    },
    section: {
      gap: v(10),
    },
    sectionHead: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      minHeight: v(24),
    },
    sectionTitle: {
      fontFamily: GTFonts.bold,
      fontSize: fs(15),
      lineHeight: fs(20),
      color: theme.colors.text,
    },
    sheetFooter: {
      flexDirection: 'row',
      gap: s(10),
      paddingHorizontal: s(16),
      paddingTop: v(12),
      borderTopWidth: 1,
      borderTopColor: theme.colors.border,
      backgroundColor: theme.colors.surface,
    },
    sheetFooterPrimary: {
      flex: 1,
    },
    optionRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: s(12),
      minHeight: v(52),
      paddingHorizontal: s(20),
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.borderSecondary,
      ...pointer,
    },
    optionLabel: {
      flex: 1,
      fontFamily: GTFonts.regular,
      fontSize: fs(15.5),
      lineHeight: fs(20),
      color: theme.colors.text,
    },
    optionLabelSelected: {
      fontFamily: GTFonts.semibold,
      color: theme.colors.primaryDark,
    },
    optionTag: {
      fontFamily: GTFonts.regular,
      fontSize: fs(12.5),
      color: theme.colors.textSecondary,
    },
    radio: {
      width: s(22),
      height: s(22),
      borderRadius: s(11),
      borderWidth: 2,
      borderColor: theme.colors.textTertiary,
      alignItems: 'center',
      justifyContent: 'center',
    },
    radioSelected: {
      borderColor: theme.colors.primaryDark,
    },
    radioDot: {
      width: s(11),
      height: s(11),
      borderRadius: s(6),
      backgroundColor: theme.colors.primaryDark,
    },
    pickerSearch: {
      paddingHorizontal: s(20),
      paddingTop: v(12),
      paddingBottom: v(8),
    },
    pickerField: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(10),
      minHeight: v(44),
      paddingHorizontal: s(14),
      borderRadius: s(12),
      backgroundColor: theme.colors.surfaceSecondary,
    },
    pickerInput: {
      flex: 1,
      minWidth: 0,
      paddingVertical: 0,
      fontFamily: GTFonts.regular,
      fontSize: fs(15),
      color: theme.colors.text,
      ...(isWeb && ({ outlineStyle: 'none' } as any)),
    },
    categoryRow: {
      flexDirection: 'row',
      alignItems: 'center',
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.borderSecondary,
      paddingRight: s(8),
    },
    categoryLabel: {
      flex: 1,
      minWidth: 0,
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(14),
      minHeight: v(52),
      paddingLeft: s(20),
      ...pointer,
    },
    categoryLabelSub: {
      minHeight: v(48),
      paddingLeft: s(56),
    },
    categoryText: {
      flex: 1,
      minWidth: 0,
      fontFamily: GTFonts.regular,
      fontSize: fs(15.5),
      lineHeight: fs(20),
      color: theme.colors.text,
    },
    categoryTextSub: {
      fontSize: fs(14.5),
      color: theme.colors.textGray600,
    },
    categoryCount: {
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(18),
      color: theme.colors.textSecondary,
      marginRight: s(10),
    },
    expandButton: {
      width: s(40),
      height: s(40),
      alignItems: 'center',
      justifyContent: 'center',
      ...pointer,
    },
    checkbox: {
      width: s(22),
      height: s(22),
      borderRadius: s(6),
      borderWidth: 2,
      borderColor: theme.colors.textTertiary,
      alignItems: 'center',
      justifyContent: 'center',
    },
    checkboxOn: {
      backgroundColor: theme.colors.primaryDark,
      borderColor: theme.colors.primaryDark,
    },
    pickerEmpty: {
      padding: s(24),
      fontFamily: GTFonts.regular,
      fontSize: fs(14),
      lineHeight: fs(20),
      color: theme.colors.textSecondary,
      textAlign: 'center',
    },

    // ---------- web side panel ----------
    panelBackdrop: {
      flex: 1,
      flexDirection: 'row',
      justifyContent: 'flex-end',
      backgroundColor: 'rgba(17, 24, 39, 0.45)',
    },
    panelScrim: {
      flex: 1,
    },
    panel: {
      width: s(420),
      maxWidth: '100%',
      height: '100%',
      backgroundColor: theme.colors.surface,
      ...(isWeb && ({ boxShadow: '-12px 0 32px rgba(0,0,0,0.16)' } as any)),
    },
    panelBody: {
      flex: 1,
    },
    panelFooter: {
      paddingBottom: v(14),
    },

    // ---------- stock value screen ----------
    // The page: a column that never scrolls. The table card at the bottom
    // takes the rest of the height and scrolls its own rows
    reportContent: {
      flex: 1,
      paddingTop: s(10),
      paddingHorizontal: s(10),
      gap: v(12),
      ...(isWeb && {
        paddingTop: s(24),
        paddingHorizontal: s(24),
        maxWidth: WEB_MAX_CONTENT_WIDTH,
        alignSelf: 'center',
        width: '100%',
      }),
    },
    summaryCard: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.md,
      padding: s(16),
      gap: v(4),
      ...theme.shadows.sm,
    },
    summaryLabel: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(13.5),
      lineHeight: fs(18),
      color: theme.colors.textSecondary,
    },
    summaryHero: {
      fontFamily: GTFonts.bold,
      fontSize: fs(30),
      lineHeight: fs(38),
      color: theme.colors.text,
    },
    summaryCaption: {
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(18),
      color: theme.colors.textSecondary,
    },
    // "Selling value" above the hero, in the main text colour
    summaryLabelPrimary: {
      color: theme.colors.text,
    },
    summaryRows: {
      marginTop: v(10),
      paddingTop: v(4),
      borderTopWidth: 1,
      borderTopColor: theme.colors.borderSecondary,
    },
    summaryRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: s(12),
      minHeight: v(32),
    },
    summaryRowLabel: {
      fontFamily: GTFonts.regular,
      fontSize: fs(14.5),
      lineHeight: fs(20),
      color: theme.colors.textGray600,
    },
    summaryRowValue: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(15),
      lineHeight: fs(20),
      color: theme.colors.text,
    },
    tiles: {
      flexDirection: 'row',
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.md,
      ...theme.shadows.sm,
    },
    tile: {
      flex: 1,
      minWidth: 0,
      paddingVertical: v(16),
      paddingHorizontal: s(20),
      gap: v(4),
    },
    tileDivider: {
      borderLeftWidth: 1,
      borderLeftColor: theme.colors.borderSecondary,
    },
    tileValue: {
      fontFamily: GTFonts.bold,
      fontSize: fs(24),
      lineHeight: fs(30),
      color: theme.colors.text,
    },
    notice: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: s(10),
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.md,
      paddingHorizontal: s(14),
      paddingVertical: v(12),
      ...theme.shadows.sm,
    },
    noticeText: {
      flex: 1,
      minWidth: 0,
      fontFamily: GTFonts.regular,
      fontSize: fs(13.5),
      lineHeight: fs(19),
      color: theme.colors.textGray600,
    },
    noticeLink: {
      fontFamily: GTFonts.semibold,
      color: theme.colors.primaryDark,
    },
    // Like Inventory's `card`: runs to the bottom of the screen, with
    // `cardClip` inside rounding the rows to its corners
    tableCard: {
      flex: 1,
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.md,
      ...theme.shadows.sm,
    },
    // No left padding: the "#" column starts at the card edge, as on Inventory
    categoryValueRow: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingRight: s(14),
      paddingVertical: v(11),
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.borderSecondary,
      // paddingRight: s(8) and ...pointer while rows opened Inventory
    },
    categoryValueRowLast: {
      borderBottomWidth: 0,
    },
    categoryValueRight: {
      flexShrink: 0,
      maxWidth: '50%',
      marginLeft: s(10),
      alignItems: 'flex-end',
    },
    // The "Buying" label under the selling value, grey like the product count
    categoryValueSecondary: {
      color: theme.colors.textSecondary,
      fontSize: fs(10),
    },
    noteDanger: {
      color: theme.colors.errorDark,
    },
    // No left padding here either: the "#" column starts at the card edge
    tableRowWide: {
      flexDirection: 'row',
      alignItems: 'center',
      minHeight: v(48),
      paddingRight: s(20),
      gap: s(12),
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.borderSecondary,
      // ...pointer while rows opened Inventory
    },
    // Same look as `listHead` (Inventory's table head)
    tableRowWideHead: tableHead.head,
    cellName: {
      flex: 1.6,
      minWidth: 0,
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(20),
      color: theme.colors.text,
    },
    cellNumber: {
      flex: 1,
      minWidth: 0,
      textAlign: 'right',
      fontFamily: GTFonts.regular,
      fontSize: fs(14),
      lineHeight: fs(20),
      color: theme.colors.text,
      fontVariant: ['tabular-nums'],
    },
    cellHead: tableHead.headText,
    cellMuted: {
      color: theme.colors.textSecondary,
    },
    cellBold: {
      fontFamily: GTFonts.semibold,
    },

    // ---------- stock value loading: each box is the height of the text it
    // stands in for, so nothing moves when the figures arrive ----------
    skeletonHero: {
      height: fs(38),
      justifyContent: 'center',
    },
    skeletonCaption: {
      height: fs(18),
      justifyContent: 'center',
    },
    skeletonRowValue: {
      height: fs(20),
      justifyContent: 'center',
      alignItems: 'flex-end',
    },
    skeletonTileValue: {
      height: fs(30),
      justifyContent: 'center',
    },
    skeletonFill: {
      flex: 1,
      minWidth: 0,
    },
    // Inside SkeletonPlaceholder, which only reads plain style objects
    skeletonRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    skeletonName: {
      height: fs(20),
      justifyContent: 'center',
    },
    skeletonMeta: {
      marginTop: v(2),
      height: fs(17),
      justifyContent: 'center',
    },
    skeletonQty: {
      height: fs(20),
      justifyContent: 'center',
      alignItems: 'flex-end',
    },
    skeletonPrice: {
      marginTop: v(3),
      height: fs(18),
      justifyContent: 'center',
      alignItems: 'flex-end',
    },
    // Stands in for the row number, centered under "#" like `rowIndex`
    skeletonIndex: {
      width: s(40),
      height: fs(20),
      justifyContent: 'center',
      alignItems: 'center',
    },
    skeletonWideRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(12),
    },
    skeletonWideName: {
      flex: 1.6,
      minWidth: 0,
      height: fs(20),
      justifyContent: 'center',
    },
    skeletonWideCell: {
      flex: 1,
      minWidth: 0,
      height: fs(20),
      justifyContent: 'center',
      alignItems: 'flex-end',
    },
  });
};

export type InventoryStyles = ReturnType<typeof createInventoryStyles>;
